/**
 * Authentication API client
 */

import { apiClient, tokenStorage } from './api'
import type { ChangePasswordRequest, LoginRequest, LoginResponse, User } from '../types'
import { logger } from '../utils/logger'

/**
 * Login with email and password
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', credentials, {
    skipAuth: true,
  })

  // Store tokens
  tokenStorage.setTokens(response.accessToken, response.refreshToken)

  return response
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshToken(): Promise<LoginResponse> {
  const currentRefreshToken = tokenStorage.getRefreshToken()

  if (!currentRefreshToken) {
    throw new Error('No refresh token available')
  }

  const response = await apiClient.post<LoginResponse>(
    '/auth/refresh',
    { refreshToken: currentRefreshToken },
    { skipAuth: true }
  )

  // Update stored tokens
  tokenStorage.setTokens(response.accessToken, response.refreshToken)

  return response
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser(): Promise<User> {
  return apiClient.get<User>('/auth/me')
}

/**
 * Logout the current user (current session).
 *
 * The API call failure is intentionally swallowed -- a user clicking "Logout"
 * has expressed intent to be logged out, and the server-side session may
 * already be invalidated (a 401 here just confirms the token is no longer
 * accepted). Swallowing keeps the rejection from cascading up to the caller
 * and producing an `Uncaught (in promise) ApiError` -- the historical FR4
 * foot-gun. Tokens are always cleared in `finally`.
 *
 * NOTE (FR4): try/catch added per /execute-feature-prp Phase 3.1. Tracked in
 * PRP §10 FUT-1 for future replacement with a structured error pattern.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout')
  } catch (error) {
    logger.warn('auth.logout', 'logout API call failed; proceeding to clear tokens', {
      error: String(error),
    })
  } finally {
    // Always clear tokens, even if the API call fails
    tokenStorage.clearTokens()
  }
}

/**
 * Logout from all sessions
 */
export async function logoutAll(): Promise<{ sessionsInvalidated: number }> {
  try {
    const response = await apiClient.post<{ sessionsInvalidated: number }>('/auth/logout-all')
    return response
  } finally {
    // Always clear tokens
    tokenStorage.clearTokens()
  }
}

/**
 * Check if user is currently authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
  return tokenStorage.hasTokens()
}

/**
 * Change the current user's password
 *
 * Requires the current password for verification.
 * New password must be at least 8 characters.
 * On success, clears the passwordMustChange flag.
 */
export async function changePassword(request: ChangePasswordRequest): Promise<void> {
  await apiClient.post('/auth/change-password', request)
}
